import { getBackendApiUrl, getCoreAPIUrl } from "../config";
import { ArtworkItemType } from "../types/api-types";
import { getBusinessCentralURLParams, isURL } from "./general";

const imageTypeMapping: Record<string, string> = {
  original: 'Original',
  productionArtwork: 'Production',
  productionArtworkPreview: 'Proof'
};

// Order + Line Item + File Category + File Role 
export const createFileName = (fileRole?: string | null, imageType?: string, extension?: string) => {
  const { artid, DocLineNo } = getBusinessCentralURLParams();
  const mappedImageType = imageType ? imageTypeMapping[imageType] : '';
  const parts = [artid, DocLineNo, mappedImageType, fileRole].filter(Boolean);
  return parts.join('_') + (extension || '');
}

export const getFileExtension = (fileName: string): string => {
  if (!fileName) {
    return '';
  }

  const match = fileName.match(/\.[0-9a-zA-Z]+$/);
  return match ? match[0] : '';
}

export const downloadProductionFile = async (
  s: string,
  fileRole?: string | null,
  imageType?: string,
  extension?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  try {
    const generatedFileName = createFileName(fileRole, imageType, extension);
    let downloadUrl = "";

    if (isURL(s)) {
      downloadUrl = s;
    } else {
      const fileBaseName = s.split('/').pop()?.replace("_thumbnail", "");
      const server = getBackendApiUrl();
      downloadUrl = `${server}/download?blobName=${fileBaseName}`;
    }

    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error('Blob download failed');
    }

    const blob = await downloadWithProgress(response, onProgress);
    triggerDownload(blob, generatedFileName);

  } catch (error) {
    console.error('Error during blob download:', error);
    throw error;
  }
}

export const downloadCxCoreOriginalFile = async (
  art: ArtworkItemType,
  onProgress?: (progress: number) => void
): Promise<void> => {
  try {
    const fileName = createFileName(art.fileRole, 'original', '.pdf');

    const modelData = encodeURIComponent(JSON.stringify({
      config: art.userFileConfig,
      Ident: {
        SessionId: art.sessionId,
        SuperUser: false
      },
      model: {
        Doc: {
          Id: art.documentId
        },
        fileType: "pdf",
        FileName: fileName
      }
    }));

    const downloadUrl = `${getCoreAPIUrl()}/Core2/Doc/Export?${modelData}`;

    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error('Blob download failed');
    }

    const blob = await downloadWithProgress(response, onProgress);
    triggerDownload(blob, fileName);

  } catch (error) {
    console.error('Error during blob download:', error);
    throw error;
  }
}

export const downloadCxCoreOriginalFileCdl = async (art:ArtworkItemType) => {
  try {
    if (!art || !art.userFileConfig || !art.sessionId || !art.documentId) {
      throw new Error('Invalid artwork data');
    }

    const modelData = encodeURIComponent(JSON.stringify({
      config: art.userFileConfig,
      Ident: {
        SessionId: art.sessionId,
        SuperUser: false
      },
      model: {
        Id: art.documentId
      }
    }));

    const downloadUrl =  `${getCoreAPIUrl()}/Core2/Doc/Read?${modelData}`;

    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error('Blob download failed');
    }

    const result = await response.json();
    
    if (!result || !result.Model.Cdl) {
      throw new Error('Invalid response format');
    }

    return result.Model.Cdl;

  } catch (error) {
    console.error('Error during blob download:', error);
  }
}

const triggerDownload = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const downloadWithProgress = async (
  response: Response,
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  const contentLength = response.headers.get('Content-Length');

  if (contentLength && onProgress) {
    const total = parseInt(contentLength, 10);
    const reader = response.body?.getReader();
    const chunks: Uint8Array[] = [];
    let receivedLength = 0;

    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
        onProgress(Math.round((receivedLength / total) * 100));
      }
      return new Blob(chunks as BlobPart[]);
    }
  }

  return response.blob();
}
